/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { StyleSheet } from '@coursera/coursera-ui';

import HowItWorksInformationCard from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/HowItWorksInformationCard';
import CatalogSection from 'bundles/enterprise-legacy-learner-home/components/single-program/CatalogSection';
import type { OnProductCardClick } from 'bundles/enterprise-legacy-learner-home/types/Products';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import MyRecentActivityForMultiprogram from 'bundles/program-home/components/enterprise-home/MyRecentActivityForMultiprogram';
import PendingInvitations from 'bundles/program-home/components/enterprise-home/PendingInvitations';
import ProgramContainer from 'bundles/program-home/components/enterprise-home/ProgramContainer';
import type {
  EnterpriseProgramFieldsFragment as EnterpriseProgram,
  ProgramInvitationFieldsFragment as ProgramInvitation,
} from 'bundles/program-home/queries/__generated__/EnterpriseHomePageQuery';

type PropsFromCaller = {
  isVisible: boolean;
  enterprisePrograms: EnterpriseProgram[];
  programInvitations: ProgramInvitation[];
  onProductCardClick: OnProductCardClick;
  isAuthenticatedUser: boolean;
  hasTSPs: boolean;
  catalogHelpCardId: string;
  catalogHelpIsExpanded: boolean;
  toggleCatalogHelpIsExpanded: () => void;
  thirdPartyOrganizationId: string;
  thirdPartyOrgSlug: string;
  userId: number;
  enableAcademicDisciplines?: boolean;
  allowOrgForCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  showSkillSetRoleFilter: boolean;
};

type Props = PropsFromCaller;

const oldAndStinkyAphroditeStyles = StyleSheet.create({
  infoCard: {
    margin: '48px auto 48px',
    maxWidth: '1200px',
  },
});

export default function HomeTabContent({
  isVisible,
  enterprisePrograms,
  programInvitations,
  onProductCardClick,
  isAuthenticatedUser,
  hasTSPs,
  catalogHelpCardId,
  catalogHelpIsExpanded,
  toggleCatalogHelpIsExpanded,
  thirdPartyOrganizationId,
  thirdPartyOrgSlug,
  userId,
  enableAcademicDisciplines,
  allowOrgForCurriculumBuilder,
  allowOrgForSpecializationConfiguration,
  showSkillSetRoleFilter,
}: Props) {
  const styles = {
    container: css`
      // Align CatalogSection margins with other tabs / footer
      .CatalogSection:first-child .CatalogSection-Margins {
        margin-top: 2.75rem;
      }

      .CatalogSection:last-child .CatalogSection-Margins {
        margin-bottom: 2.75rem;
      }
    `,
  };

  return (
    <div className="rc-HomeTabContent" css={styles.container}>
      <CatalogSection>
        <HowItWorksInformationCard
          // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
          rootClassName={oldAndStinkyAphroditeStyles.infoCard}
          id={catalogHelpCardId}
          isOpen={catalogHelpIsExpanded}
          onRequestClose={toggleCatalogHelpIsExpanded}
          userAuthenticated={isAuthenticatedUser}
          hasTSPs={hasTSPs}
        />
        <PendingInvitations programInvitations={programInvitations} />
        <MyRecentActivityForMultiprogram
          thirdPartyOrganizationId={thirdPartyOrganizationId}
          thirdPartyOrgSlug={thirdPartyOrgSlug}
          userId={userId}
        />
      </CatalogSection>

      {enterprisePrograms.map((enterpriseProgram, index) => (
        <CatalogSection key={enterpriseProgram.id}>
          <ProgramContainer
            enterpriseProgram={enterpriseProgram}
            isAuthenticatedUser={Boolean(userId)}
            onProductCardClick={onProductCardClick}
            lazy={index > 0}
            isVisible={isVisible}
            enableAcademicDiscipline={
              enableAcademicDisciplines && Boolean(enterpriseProgram.metadata?.linkedAcademicDisciplineIds?.length)
            }
            allowOrgForCurriculumBuilder={allowOrgForCurriculumBuilder}
            allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
            showSkillSetRoleFilter={showSkillSetRoleFilter}
          />
        </CatalogSection>
      ))}
    </div>
  );
}
